import {
  measuredModuleCount,
  orphanModules,
  workerFullName,
  workerInitials,
} from '@insolitum/utils';
import {
  AlertCircle,
  ArrowLeft,
  Award,
  Briefcase,
  CalendarDays,
  CheckCircle,
  Clock,
  Globe,
  HardHat,
  Layers,
  Loader2,
  Mail,
  Phone,
  Trophy,
  XCircle,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ModuleAccessChips } from '../../components/admin/workers/ModuleAccessChips';
import { ModuleScorecard } from '../../components/admin/workers/ModuleScorecard';
import { formatRelativeDate } from '../../components/admin/workers/tone';
import {
  setWorkerModuleAccess,
  useWorkerDossier,
  WORKER_WINDOW_LABELS,
  WORKER_WINDOW_OPTIONS,
  type WorkerWindowDays,
} from '../../hooks/useWorkerInsights';
import { supabase } from '../../lib/supabase';
import { getModuleDef } from '../../modules/registry';
import { useAuth } from '../../providers/AuthProvider';

/**
 * Kartoteka pracownika — `/admin/workers/:workerId`
 * =================================================
 *
 * „Dane pracowników w głównym katalogu Universe powinny pokazywać kartoteki
 * pracowników oraz ich wyniki i dane z WSZYSTKICH modułów, do których mają
 * dostęp i w których są mierzeni."
 *
 * Do tej pory najgłębszym widokiem pracownika w powłoce był modal z odznakami.
 * Modal nie ma adresu: nie da się go podesłać kierownikowi, otworzyć w nowej
 * karcie ani wrócić do niego z modułu. Kartoteka jest TRASĄ właśnie dlatego —
 * i dlatego moduł może w nią wejść wprost, zamiast wyrzucać człowieka na
 * nieprzefiltrowaną listę.
 *
 * Kolejność sekcji jest stała, bo kartoteka jest narzędziem codziennym i uczy
 * się jej pamięcią mięśniową:
 *   1. Kim jest (tożsamość + gdzie jest mierzony)
 *   2. Wyniki organ po organie
 *   3. Kadry
 *   4. Dorobek (odznaki)
 *   5. Oś czasu
 *
 * Czego tu NIE MA: jednej liczby podsumowującej człowieka. Uśrednienie
 * dokładności temperatury z terminowością zadań i punktami daje ocenę, której
 * nikt nie obroni — i przestaje być kartoteką, a zaczyna być zautomatyzowaną
 * oceną pracownika (RODO art. 22). Liczby stoją obok siebie, podpisane organem.
 */

interface AchievementRow {
  id: string;
  name: string;
  description: string | null;
  tier: string | null;
  points_awarded: number | null;
  earned_at: string;
}

interface ActivityRow {
  id: string;
  activity_type: string | null;
  module: string | null;
  points_earned: number | null;
  occurred_at: string;
}

const TIER_COLOR: Record<string, string> = {
  bronze: 'text-amber-600 bg-amber-600/20',
  silver: 'text-gray-300 bg-gray-300/20',
  gold: 'text-yellow-400 bg-yellow-400/20',
  platinum: 'text-cyan-300 bg-cyan-300/20',
  diamond: 'text-purple-400 bg-purple-400/20',
};

const ROLE_LABEL: Record<string, string> = {
  trainee: 'Praktykant',
  worker: 'Pracownik',
  senior: 'Starszy pracownik',
  supervisor: 'Brygadzista',
  expert: 'Ekspert',
};

const EXPERIENCE_LABEL: Record<string, string> = {
  novice: 'Początkujący',
  intermediate: 'Średniozaawansowany',
  expert: 'Ekspert',
};

/** Staż w pełnych miesiącach/latach — z `hire_date`, a gdy go brak, z `tenure_start_date`. */
function formatTenure(hireDate: string | null, tenureStart: string | null): string | null {
  const source = hireDate || tenureStart;
  if (!source) return null;
  const start = new Date(source);
  if (Number.isNaN(start.getTime())) return null;
  const months = Math.max(
    0,
    Math.floor((Date.now() - start.getTime()) / (30.44 * 24 * 3600 * 1000))
  );
  if (months < 1) return 'poniżej miesiąca';
  if (months < 12) return `${months} mies.`;
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return rest === 0 ? `${years} lat(a)` : `${years} lat(a) ${rest} mies.`;
}

export function WorkerDossierPage() {
  const { workerId } = useParams<{ workerId: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManage = hasRole(['owner', 'admin', 'manager']);

  const [windowDays, setWindowDays] = useState<WorkerWindowDays>(30);
  const { row, isLoading, error, statsUnavailable, refresh } = useWorkerDossier(
    workerId,
    windowDays
  );

  const [achievements, setAchievements] = useState<AchievementRow[]>([]);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [assignError, setAssignError] = useState<string | null>(null);

  useEffect(() => {
    if (!workerId) return;
    let cancelled = false;

    const load = async () => {
      const [ach, act] = await Promise.all([
        supabase
          .from('worker_achievements')
          .select('id, name, description, tier, points_awarded, earned_at')
          .eq('worker_id', workerId)
          .order('earned_at', { ascending: false })
          .limit(50),
        supabase
          .from('worker_activity_log')
          .select('id, activity_type, module, points_earned, occurred_at')
          .eq('worker_id', workerId)
          .order('occurred_at', { ascending: false })
          .limit(30),
      ]);
      if (cancelled) return;
      // Dorobek i oś czasu są DODATKIEM do kartoteki — gdy ich zapytania padną,
      // kartoteka zostaje na ekranie z resztą treści. Milczące [] jest tu
      // właściwą degradacją, bo alternatywą jest pusty ekran zamiast kartoteki.
      setAchievements((ach.data ?? []) as AchievementRow[]);
      setActivity((act.data ?? []) as ActivityRow[]);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [workerId]);

  const handleToggleModule = async (moduleSlug: string, enabled: boolean) => {
    if (!workerId) return;
    setAssignError(null);
    try {
      await setWorkerModuleAccess(workerId, [moduleSlug], enabled ? 'add' : 'remove');
      await refresh();
    } catch (err) {
      console.error('[WorkerDossier] przypisanie modułu', err);
      setAssignError('Nie udało się zmienić przypisania do modułu');
    }
  };

  const measured = useMemo(() => (row ? measuredModuleCount(row.modules) : 0), [row]);
  const orphans = useMemo(() => (row ? orphanModules(row.modules) : []), [row]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error || !row) {
    return (
      <div className="space-y-4">
        <Link
          to="/admin/workers"
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-aurora-text"
        >
          <ArrowLeft className="w-4 h-4" />
          Wróć do kartoteki
        </Link>
        <div className="bg-red-500/10 border border-red-500/30 rounded-ids-md p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <p className="text-red-400">{error || 'Nie znaleziono pracownika'}</p>
        </div>
      </div>
    );
  }

  const { worker, modules } = row;
  const name = workerFullName(worker);
  const tenure = formatTenure(worker.hire_date, worker.tenure_start_date);
  const assignedCards = modules.filter((m) => m.has_access || m.is_measured);
  const otherCards = modules.filter((m) => !m.has_access && !m.is_measured);

  return (
    <div className="space-y-6">
      {/* ── Powrót + okno czasowe ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => navigate('/admin/workers')}
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-aurora-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Kartoteka pracowników
        </button>
        <div className="flex items-center gap-1 bg-gray-800 border border-gray-700 rounded-ids-md p-1">
          {WORKER_WINDOW_OPTIONS.map((days) => (
            <button
              key={days}
              onClick={() => setWindowDays(days)}
              className={`px-3 py-1 rounded-ids-sm text-xs font-medium transition-colors ${
                windowDays === days
                  ? 'bg-orange-600 text-aurora-on-accent'
                  : 'text-gray-400 hover:text-aurora-text hover:bg-gray-700'
              }`}
            >
              {WORKER_WINDOW_LABELS[days]}
            </button>
          ))}
        </div>
      </div>

      {/* ── 1. Tożsamość + gdzie jest mierzony ── */}
      <div className="bg-gray-800 rounded-ids-lg border border-gray-700 p-5">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="w-16 h-16 rounded-ids-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {worker.avatar_url ? (
              <img src={worker.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xl font-bold text-aurora-text">{workerInitials(worker)}</span>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-2xl font-bold text-aurora-text">{name}</h1>
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium ${
                  worker.is_active ? 'text-emerald-400' : 'text-red-400'
                }`}
              >
                {worker.is_active ? (
                  <CheckCircle className="w-3.5 h-3.5" />
                ) : (
                  <XCircle className="w-3.5 h-3.5" />
                )}
                {worker.is_active ? 'Aktywny' : 'Nieaktywny'}
              </span>
            </div>
            <p className="text-sm text-gray-400 mt-0.5">
              {worker.position || ROLE_LABEL[worker.worker_role ?? ''] || 'Pracownik produkcyjny'}
              {tenure && <span className="text-gray-600"> · staż {tenure}</span>}
            </p>

            <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-xs text-gray-400">
              {worker.email && (
                <span className="flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  {worker.email}
                </span>
              )}
              {worker.phone && (
                <span className="flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" />
                  {worker.phone}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                Ostatnie logowanie: {formatRelativeDate(worker.last_login_at)}
              </span>
            </div>

            <div className="mt-4">
              <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">
                Gdzie jest mierzony —{' '}
                <span className="text-gray-400">
                  {measured} z {modules.length} organów
                </span>
              </p>
              <ModuleAccessChips
                scorecards={modules}
                canManage={canManage}
                onToggle={handleToggleModule}
                size="md"
              />
            </div>
          </div>
        </div>

        {assignError && (
          <div className="mt-4 flex items-center gap-2 text-sm text-red-400">
            <AlertCircle className="w-4 h-4" />
            {assignError}
          </div>
        )}

        {orphans.length > 0 && (
          <div className="mt-4 flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/25 rounded-ids-md">
            <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-300">
              Ten pracownik ma dane w organach, do których nie jest przypisany:{' '}
              <strong>{orphans.map((s) => getModuleDef(s)?.navName ?? s).join(', ')}</strong>. Przez
              to nie widać go na ich listach pracowników — kliknij plakietkę, żeby to naprawić.
            </p>
          </div>
        )}
      </div>

      {statsUnavailable && (
        <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/25 rounded-ids-md text-xs text-amber-300">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          Nie udało się policzyć wyników w modułach. Dane kadrowe poniżej są aktualne.
        </div>
      )}

      {/* ── 2. Wyniki organ po organie ── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Layers className="w-4 h-4 text-orange-400" />
          <h2 className="text-sm font-semibold text-aurora-text uppercase tracking-wider">
            Wyniki w modułach
          </h2>
          <span className="text-xs text-gray-500">
            okno {WORKER_WINDOW_LABELS[windowDays]} · każdy organ liczy po swojemu, liczby nie są
            sumowane
          </span>
        </div>

        {assignedCards.length === 0 ? (
          <div className="bg-gray-800 rounded-ids-lg border border-gray-700 p-8 text-center">
            <Layers className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-aurora-text font-medium mb-1">
              Pracownik nie jest przypisany do żadnego organu
            </p>
            <p className="text-sm text-gray-500">
              Kliknij plakietkę powyżej, żeby go przypisać — wtedy pojawi się na liście pracowników
              tego modułu.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {assignedCards.map((card) => (
              <ModuleScorecard
                key={card.module}
                card={card}
                workerName={name}
                canManage={canManage}
                onAssign={(slug) => void handleToggleModule(slug, true)}
              />
            ))}
          </div>
        )}

        {otherCards.length > 0 && canManage && (
          <div className="mt-4 p-4 bg-gray-800/50 rounded-ids-lg border border-dashed border-gray-700">
            <p className="text-xs text-gray-500 mb-2">
              Pozostałe organy — kliknij, żeby przypisać pracownika:
            </p>
            <ModuleAccessChips
              scorecards={otherCards}
              canManage={canManage}
              onToggle={handleToggleModule}
            />
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* ── 3. Kadry ── */}
        <div className="bg-gray-800 rounded-ids-lg border border-gray-700 p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-aurora-text mb-4">
            <Briefcase className="w-4 h-4 text-gray-400" />
            Kadry
          </h2>
          <dl className="space-y-3 text-sm">
            <Field
              label="Rola"
              value={ROLE_LABEL[worker.worker_role ?? ''] ?? '—'}
              icon={HardHat}
            />
            <Field
              label="Doświadczenie"
              value={EXPERIENCE_LABEL[worker.experience_level ?? ''] ?? '—'}
              icon={Award}
            />
            <Field
              label="Język komunikacji"
              value={(worker.preferred_language ?? 'pl').toUpperCase()}
              icon={Globe}
            />
            <Field
              label="Zatrudniony od"
              value={
                worker.hire_date ? new Date(worker.hire_date).toLocaleDateString('pl-PL') : '—'
              }
              icon={CalendarDays}
            />
          </dl>
          <p className="text-[11px] text-gray-600 mt-4 leading-snug">
            Dane płacowe i PIN celowo nie są częścią kartoteki — edytuje się je w formularzu
            pracownika.
          </p>
        </div>

        {/* ── 4. Dorobek ── */}
        <div className="bg-gray-800 rounded-ids-lg border border-gray-700 p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-aurora-text mb-4">
            <Trophy className="w-4 h-4 text-purple-400" />
            Dorobek
            <span className="text-xs text-gray-500 font-normal">({achievements.length})</span>
          </h2>
          {achievements.length === 0 ? (
            <p className="text-sm text-gray-500">
              Brak odznak. Zdobywa się je automatycznie podczas pracy.
            </p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {achievements.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-2.5 p-2 bg-gray-700/40 rounded-ids-md"
                >
                  <span
                    className={`p-1.5 rounded-ids-md flex-shrink-0 ${
                      TIER_COLOR[a.tier ?? ''] ?? 'text-gray-400 bg-gray-400/20'
                    }`}
                  >
                    <Trophy className="w-3.5 h-3.5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-aurora-text truncate">{a.name}</p>
                    <p className="text-[11px] text-gray-500 truncate">{a.description}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-medium text-yellow-400">+{a.points_awarded ?? 0}</p>
                    <p className="text-[10px] text-gray-600">
                      {new Date(a.earned_at).toLocaleDateString('pl-PL')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── 5. Oś czasu ── */}
        <div className="bg-gray-800 rounded-ids-lg border border-gray-700 p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-aurora-text mb-4">
            <Clock className="w-4 h-4 text-cyan-400" />
            Oś czasu
          </h2>
          {activity.length === 0 ? (
            <p className="text-sm text-gray-500">Brak zapisanych zdarzeń.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {activity.map((entry) => {
                const def = entry.module ? getModuleDef(entry.module) : undefined;
                return (
                  <div key={entry.id} className="flex items-start gap-2.5 text-xs">
                    <span
                      className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        def ? def.color.bg.replace('/20', '') : 'bg-gray-600'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-300 truncate">{entry.activity_type ?? 'Zdarzenie'}</p>
                      <p className="text-[11px] text-gray-600">
                        {def?.navName ?? entry.module ?? 'Universe'} ·{' '}
                        {formatRelativeDate(entry.occurred_at)}
                      </p>
                    </div>
                    {!!entry.points_earned && (
                      <span className="flex items-center gap-0.5 text-yellow-400 flex-shrink-0">
                        <Zap className="w-3 h-3" />
                        {entry.points_earned}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: typeof HardHat;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="flex items-center gap-2 text-gray-500">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </dt>
      <dd className="text-gray-200 text-right truncate">{value}</dd>
    </div>
  );
}
